/**
 * What one participant's call was like, added up over the call, for the
 * super admin's call report (Admin -> Call reports). Fed by
 * `VoicePathMonitor` every few seconds; sent to
 * `POST /rooms/<id>/call-quality/` once a minute and on leaving.
 *
 * The report also carries the outage events measured by `OutageTracker`
 * (real start/end times, re-sent each time so the server can upsert them by
 * id) and, on the final report of a deliberate end, `ended: "left"`.
 *
 * Only counts and durations -- never what was said.
 */
import type { OutageEvent } from "./outageTracker";
import { detectInAppBrowser } from "./inAppBrowser";
import type { ConnectionQualityValue, NetworkState, VoicePath } from "./voicePath";

/** Report once a minute: a crash or closed tab loses at most that much. */
export const QUALITY_REPORT_INTERVAL_MS = 60_000;
/** The first report comes early, so a live call's report fills in quickly. */
export const FIRST_QUALITY_REPORT_MS = 15_000;

export interface CallQualityReport {
  participant_sid?: string;
  seconds: number;
  network: { good_s: number; weak_s: number; down_s: number; unknown_s: number };
  loss_avg: number | null;
  loss_max: number | null;
  rtt_avg: number | null;
  rtt_max: number | null;
  quality_worst: ConnectionQualityValue;
  reconnects: number;
  sound_blocked_s: number;
  mic_silent_s: number;
  /** As a speaker: listener identity -> seconds they were not receiving you. */
  not_heard_by: Record<string, number>;
  /** As a listener: speaker identity -> seconds their audio was not arriving here. */
  not_receiving: Record<string, number>;
  /**
   * As a listener: speaker identity -> seconds they talked while nothing
   * audible played here (#2223, lib/audibility.ts). Unlike `not_receiving`
   * this catches audio that arrived but was silent. Sent only when non-empty.
   */
  not_audible?: Record<string, number>;
  browser: string;
  /** Every outage event since the participation began; omitted when there is none. */
  outages?: OutageEvent[];
  /** Only on the final report of a deliberate end (Leave, tab close, reload). */
  ended?: "left";
}

export interface QualitySample {
  /** Time since the previous sample. */
  elapsedMs: number;
  path: VoicePath;
  /** Speakers whose audio is not arriving here (this listener's own view). */
  notArriving: string[];
  /** Speakers talking but not audible here (open "couldn't hear" stretches). */
  notAudible?: string[];
  connected: boolean;
  /** The browser lets call audio play (autoplay not blocking). */
  canPlayAudio: boolean;
}

const QUALITY_RANK: Record<ConnectionQualityValue, number> = { unknown: -1, excellent: 0, good: 1, poor: 2, lost: 3 };
const NETWORK_KEY: Record<NetworkState, keyof CallQualityReport["network"]> = {
  good: "good_s",
  weak: "weak_s",
  down: "down_s",
  unknown: "unknown_s",
};

export class CallQualityTally {
  private ms = 0;
  private network = { good_s: 0, weak_s: 0, down_s: 0, unknown_s: 0 };
  private loss = { sum: 0, n: 0, max: null as number | null };
  private rtt = { sum: 0, n: 0, max: null as number | null };
  private worst: ConnectionQualityValue = "unknown";
  private reconnects = 0;
  private blockedMs = 0;
  private silentMs = 0;
  private notHeardBy: Record<string, number> = {};
  private notReceiving: Record<string, number> = {};
  private notAudible: Record<string, number> = {};

  add({ elapsedMs, path, notArriving, notAudible = [], connected, canPlayAudio }: QualitySample): void {
    const ms = Math.max(0, Math.min(elapsedMs, 60_000)); // a sleeping laptop is not a minute-long problem
    this.ms += ms;
    this.network[NETWORK_KEY[path.network]] += ms;
    if (path.lossPct !== null) {
      this.loss.sum += path.lossPct;
      this.loss.n += 1;
      this.loss.max = Math.max(this.loss.max ?? 0, path.lossPct);
    }
    if (path.rttMs !== null) {
      this.rtt.sum += path.rttMs;
      this.rtt.n += 1;
      this.rtt.max = Math.max(this.rtt.max ?? 0, path.rttMs);
    }
    if (QUALITY_RANK[path.quality] > QUALITY_RANK[this.worst]) this.worst = path.quality;
    if (connected && !canPlayAudio) this.blockedMs += ms;
    if (path.mic === "silent") this.silentMs += ms;
    for (const l of path.listeners) {
      // "inaudible": their audio arrived but nothing audible played there --
      // still not heard (#2223 AC45).
      if (l.state === "missing" || l.state === "inaudible") {
        this.notHeardBy[l.identity] = (this.notHeardBy[l.identity] ?? 0) + ms;
      }
    }
    for (const id of notArriving) this.notReceiving[id] = (this.notReceiving[id] ?? 0) + ms;
    for (const id of notAudible) this.notAudible[id] = (this.notAudible[id] ?? 0) + ms;
  }

  /** Time covered so far; 0 means there is nothing worth reporting. */
  get observedMs(): number {
    return this.ms;
  }

  reconnected(): void {
    this.reconnects += 1;
  }

  report(
    participantSid: string,
    browser: string,
    extra: { outages?: OutageEvent[]; ended?: "left" } = {},
  ): CallQualityReport {
    const s = (ms: number) => Math.round(ms / 1000);
    const secondsOf = (m: Record<string, number>) =>
      Object.fromEntries(Object.entries(m).map(([k, v]) => [k, s(v)]).filter(([, v]) => (v as number) > 0));
    return {
      participant_sid: participantSid,
      seconds: s(this.ms),
      network: {
        good_s: s(this.network.good_s),
        weak_s: s(this.network.weak_s),
        down_s: s(this.network.down_s),
        unknown_s: s(this.network.unknown_s),
      },
      loss_avg: this.loss.n ? Math.round((this.loss.sum / this.loss.n) * 10) / 10 : null,
      loss_max: this.loss.max === null ? null : Math.round(this.loss.max * 10) / 10,
      rtt_avg: this.rtt.n ? Math.round(this.rtt.sum / this.rtt.n) : null,
      rtt_max: this.rtt.max,
      quality_worst: this.worst,
      reconnects: this.reconnects,
      sound_blocked_s: s(this.blockedMs),
      mic_silent_s: s(this.silentMs),
      not_heard_by: secondsOf(this.notHeardBy),
      not_receiving: secondsOf(this.notReceiving),
      ...(Object.keys(secondsOf(this.notAudible)).length ? { not_audible: secondsOf(this.notAudible) } : {}),
      browser,
      ...(extra.outages && extra.outages.length ? { outages: extra.outages } : {}),
      ...(extra.ended ? { ended: extra.ended } : {}),
    };
  }
}

// Order matters: Edge's user agent also says Chrome, and Chrome's says Safari.
// On iPhone and iPad every browser is Safari underneath and names itself with
// its own token (CriOS, FxiOS, EdgiOS) instead of the desktop one.
const BROWSERS: Array<[string, RegExp]> = [
  ["Edge", /EdgiOS\/(\d+)/],
  ["Chrome", /CriOS\/(\d+)/],
  ["Firefox", /FxiOS\/(\d+)/],
  ["Opera", /OPT\/(\d+)/],
  ["Edge", /Edg\/(\d+)/],
  ["Firefox", /Firefox\/(\d+)/],
  // Chromium-based browsers also say "Chrome/"; their own token has to win
  // (#2223 AC27: Samsung Internet 30 was labelled "Chrome 143 on Android").
  ["Samsung Internet", /SamsungBrowser\/(\d+)/],
  ["Opera", /OPR\/(\d+)/],
  ["Chrome", /Chrome\/(\d+)/],
  ["Safari", /Version\/(\d+).*Safari/],
];
// iOS before macOS: an iPad's user agent claims to be a Mac.
const SYSTEMS: Array<[string, RegExp]> = [
  ["iOS", /iPhone|iPad|iPod/],
  ["Android", /Android/],
  ["macOS", /Mac OS X|Macintosh/],
  ["Windows", /Windows/],
  ["Linux", /Linux/],
];

/**
 * "Chrome 140 on macOS" -- enough to spot a browser pattern, no fingerprint.
 * Built-in browsers of other apps are called out ("Instagram in-app browser
 * on iOS", or "In-app browser on …" when the app does not name itself),
 * because calls fail there in ways a real browser does not.
 */
export function describeBrowser(userAgent: string): string {
  const system = SYSTEMS.find(([, re]) => re.test(userAgent))?.[0] ?? "another system";
  if (/\bWeCoDesktop\//.test(userAgent)) return `WeCo desktop app on ${system}`;
  const inApp = detectInAppBrowser(userAgent);
  if (inApp) return `${inApp.app ? `${inApp.app} in-app browser` : "In-app browser"} on ${system}`;
  let browser = "Other browser";
  for (const [name, re] of BROWSERS) {
    const version = re.exec(userAgent)?.[1];
    if (version) {
      browser = `${name} ${version}`;
      break;
    }
  }
  return `${browser} on ${system}`;
}

/**
 * One tally per LiveKit participant sid. The shell keeps one `<LiveKitRoom>`
 * and only swaps the token on a full rejoin, so the monitor outlives the
 * participation it measures; without this the second stretch would report the
 * first stretch's time and reconnects again and the call report would count
 * them twice (each stretch's row is summed per person).
 */
export class StretchTally {
  private current = new CallQualityTally();

  constructor(private sid: string = "") {}

  get tally(): CallQualityTally {
    return this.current;
  }

  /** The sid the current tally belongs to ("" until one was seen). */
  get stretchSid(): string {
    return this.sid;
  }

  /**
   * Call with the sid now in use. When it changed, the finished stretch (its
   * sid and tally, for one last report) comes back and a fresh tally starts.
   */
  roll(sid: string | undefined): { sid: string; tally: CallQualityTally } | null {
    if (!sid || sid === this.sid) return null;
    if (!this.sid) {
      this.sid = sid;
      return null;
    }
    const done = { sid: this.sid, tally: this.current };
    this.sid = sid;
    this.current = new CallQualityTally();
    return done;
  }
}

/**
 * Tells an in-place recovery apart from a full restart, from the Room events
 * livekit-client fires (checked against 2.23's Room.ts):
 *
 * - resume (same participant sid): `SignalReconnecting` ... `Reconnected`
 * - restart (new participant sid): `Reconnecting` ... `Reconnected`
 *
 * A resume can turn into a restart partway, so the decision is made at
 * `Reconnected`: it counts only when the attempt never became a restart. A
 * restart is not counted here because its new sid already makes it a drop in
 * the call report; counting `Reconnecting` (as before) both missed every
 * resume and double-counted restarts.
 */
export class InPlaceRecoveryWatch {
  private mode: "idle" | "resume" | "restart" = "idle";

  signalReconnecting(): void {
    if (this.mode === "idle") this.mode = "resume";
  }

  reconnecting(): void {
    this.mode = "restart";
  }

  /** True when the reconnection that just finished was an in-place resume. */
  reconnected(): boolean {
    const inPlace = this.mode === "resume";
    this.mode = "idle";
    return inPlace;
  }
}
